import { useEffect, useLayoutEffect, useMemo } from "react";
import { View } from "react-native";
import type { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { getRelatedBlocks } from "@src/packages/content";
import { useLocale } from "@src/packages/i18n";
import type { ChatStackParamList, MainTabParamList } from "../../types";
import { ProcedureDetailContent } from "../../components/ProcedureDetailSheet";
import { getFactSheetRouteBlock } from "../../components/factSheetRouteIds";
import type { FactSheetLinkTarget } from "../../components/LinkedFactSheetText";
import { useSmartBack } from "../../hooks/useSmartBack";
import EndometrialBiopsyFactsheetScreen from "./EndometrialBiopsyFactsheetScreen";
import { getTestRouteTarget } from "../access/testRouteIds";

type Props = NativeStackScreenProps<ChatStackParamList, "ProcedureDetail">;

export default function ProcedureDetailScreen({ route, navigation }: Props) {
  const { procedureId, returnTab } = route.params;
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Procedures",
  });
  const rootNavigation =
    navigation.getParent<BottomTabNavigationProp<MainTabParamList>>();

  const procedure = useMemo(() => {
    void locale;
    return getFactSheetRouteBlock("procedure", procedureId);
  }, [procedureId, locale]);

  const canonicalTest = useMemo(() => {
    if (!procedure || procedure.id === "proc-endometrial-biopsy") return null;

    return (
      getRelatedBlocks(procedure.id, "related-to").find(
        ({ block, relationship }) =>
          block.kind === "test" && relationship.label === "Canonical test",
      )?.block ?? null
    );
  }, [procedure]);

  useEffect(() => {
    if (!procedure) {
      navigation.goBack();
    }
  }, [procedure, navigation]);

  useEffect(() => {
    if (!canonicalTest) return;

    const target = getTestRouteTarget(String(canonicalTest.id));
    if (target.screen === "TestProductDetail") {
      rootNavigation?.navigate("Access", {
        screen: "TestProductDetail",
        params: target.params,
      });
      return;
    }

    rootNavigation?.navigate("Access", {
      screen: "TestDetail",
      params: target.params,
    });
  }, [canonicalTest, rootNavigation]);

  useLayoutEffect(() => {
    if (procedure) {
      navigation.setOptions({ title: procedure.title });
    }
  }, [procedure, navigation]);

  const openFactSheetLink = (target: FactSheetLinkTarget) => {
    switch (target.kind) {
      case "condition":
        navigation.navigate("ConditionDetail", { conditionId: target.id });
        return;
      case "medication":
        navigation.navigate("MedicationDetail", { medicationId: target.id });
        return;
      case "procedure":
        navigation.navigate("ProcedureDetail", { procedureId: target.id });
        return;
      case "test":
        navigation.navigate("TestDetail", { testId: target.id });
        return;
      case "supplement":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Access",
            params: {
              screen: "SupplementDetail",
              params: { supplementId: target.id },
            },
          }),
        );
        return;
      case "therapy":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Access",
            params: {
              screen: "TherapyDetail",
              params: { therapyId: target.id },
            },
          }),
        );
        return;
    }
  };

  if (!procedure || canonicalTest) return null;

  if (procedure.id === "proc-endometrial-biopsy") {
    return (
      <EndometrialBiopsyFactsheetScreen route={route} navigation={navigation} />
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <ProcedureDetailContent
        item={procedure}
        onClose={handleBack}
        onOpenProgram={(programId) =>
          rootNavigation?.navigate("Access", {
            screen: "ProgramDetail",
            params: { programId, returnTab: "Learn" },
          })
        }
        onOpenFactSheet={openFactSheetLink}
        skipExitAnimation
      />
    </View>
  );
}
